'use client'

import { cn } from '@/lib/utils'
import { X } from 'lucide-react'

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent'

export interface BadgeProps {
  variant?: BadgeVariant
  dot?: boolean
  removable?: boolean
  onRemove?: () => void
  children: React.ReactNode
  className?: string
}

const variantStyles: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  default: { bg: 'var(--surface-tertiary)', text: 'var(--text-secondary)', dot: 'var(--text-tertiary)' },
  success: { bg: 'var(--bg-success)', text: 'var(--fg-success)', dot: 'var(--fg-success)' },
  warning: { bg: 'var(--bg-warning)', text: 'var(--fg-warning)', dot: 'var(--fg-warning)' },
  danger: { bg: 'var(--bg-danger)', text: 'var(--fg-danger)', dot: 'var(--fg-danger)' },
  info: { bg: 'var(--bg-info)', text: 'var(--fg-info)', dot: 'var(--fg-info)' },
  accent: { bg: 'var(--accent-muted)', text: 'var(--accent-emphasis)', dot: 'var(--accent)' },
}

export function Badge({ variant = 'default', dot, removable, onRemove, children, className }: BadgeProps) {
  const s = variantStyles[variant]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-[11px] font-medium',
        className,
      )}
      style={{ background: s.bg, color: s.text }}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />}
      {children}
      {removable && (
        <button
          onClick={onRemove}
          className="rounded-full ml-0.5 hover:opacity-70"
          style={{ color: s.text }}
        >
          <X size={11} strokeWidth={2.5} />
        </button>
      )}
    </span>
  )
}

export interface TagProps {
  removable?: boolean
  onRemove?: () => void
  children: React.ReactNode
  className?: string
}

export function Tag({ removable, onRemove, children, className }: TagProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-6 px-2 rounded-md text-[12px] font-medium',
        className,
      )}
      style={{ background: 'var(--surface-tertiary)', color: 'var(--text-primary)' }}
    >
      {children}
      {removable && (
        <button
          onClick={onRemove}
          className="rounded-full hover:bg-[var(--surface-active)]"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <X size={12} strokeWidth={2} />
        </button>
      )}
    </span>
  )
}
